import { PwInput } from "@/components/ui/pw-input";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Requests that failed with an expired session wait here until the user signs in again.
const queue = [];
const EXPIRED = /^(Token expired|Invalid token|Not authenticated)$/i;
const SKIP = /\/auth\/(login|register|renew)/;

export const queueForReauth = (err) => {
  const cfg = err.config;
  const d = err.response?.data?.detail;
  if (err.response?.status !== 401 || !cfg || cfg._reauth || SKIP.test(cfg.url || "") || !EXPIRED.test(String(d || ""))) return null;
  return new Promise((resolve, reject) => {
    queue.push({ cfg, resolve, reject, err });
    window.dispatchEvent(new Event("reauth-needed"));
  });
};

const flush = (token) => {
  queue.splice(0).forEach(({ cfg, resolve, reject }) => {
    if (!token) return undefined; // logging out: drop silently, no stale error toasts
    cfg._reauth = true;
    cfg.headers = { ...(cfg.headers?.toJSON ? cfg.headers.toJSON() : cfg.headers), Authorization: `Bearer ${token}` };
    axios(cfg).then(resolve, reject);
  });
};

export const ReauthDialog = ({ onGiveUp }) => {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener("reauth-needed", on);
    return () => window.removeEventListener("reauth-needed", on);
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email: user.email, password: pwd });
      localStorage.setItem("token", r.data.token);
      localStorage.setItem("user", JSON.stringify(r.data.user));
      setOpen(false); setPwd("");
      toast.success("Signed in again — finishing your last action");
      flush(r.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign in");
    } finally { setBusy(false); }
  };
  const giveUp = () => { setOpen(false); flush(null); onGiveUp(); };
  return (
    <Dialog open={open}>
      <DialogContent className="max-w-sm z-[200] [&>button.absolute]:hidden" data-testid="reauth-dialog"
        onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> Session ended — sign in again</DialogTitle>
          <DialogDescription>Your work is kept. Enter your password and the action you were doing will be saved.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs text-slate-600" data-testid="reauth-email">{user.email}</div>
          <PwInput autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Password"
            className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="reauth-password" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={giveUp} className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="reauth-logout">Log out</button>
            <button type="submit" disabled={busy || !pwd} data-testid="reauth-submit"
              className="h-9 px-4 rounded-md bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in &amp; continue</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
